import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ArrowUp, Download, Image as ImageIcon, Lock, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import toolPages from '../lib/toolPages';
import { getToolTone } from '../lib/toolVisuals';
import Seo, { DEFAULT_LOGO, SITE_URL } from './Seo';

const defaultFaq = [
  {
    question: 'How does the compression work?',
    answer:
      'The tool resizes large images when needed and exports a new optimized file using browser image APIs.',
  },
  {
    question: 'Are my images uploaded?',
    answer:
      'No. All image processing happens directly in your browser, so your image files stay on your device.',
  },
  {
    question: 'Is there a file size limit?',
    answer:
      'There is no strict upload limit, but files under 10MB usually process faster and more reliably in the browser.',
  },
];

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Unable to create optimized image'));
        }
      },
      type,
      quality
    );
  });

const getScaledDimensions = (img, maxDimension) => {
  let width = img.width;
  let height = img.height;

  if (width > maxDimension || height > maxDimension) {
    if (width > height) {
      height = Math.round((height * maxDimension) / width);
      width = maxDimension;
    } else {
      width = Math.round((width * maxDimension) / height);
      height = maxDimension;
    }
  }

  return { width, height };
};

const ImageOptimizer = ({
  title = 'Image Compressor',
  subtitle = 'Optimize your images for web use instantly.',
  metaTitle = 'Online Image Compression Tool | CompressImages.org',
  metaDescription = 'Compress JPEG, PNG, WebP, and more directly in your browser. Fast, secure, and completely free.',
  canonicalPath = '/tool',
  accept = 'image/*',
  acceptedTypes,
  outputType = 'image/jpeg',
  outputExtension = 'jpg',
  outputLabel = 'Optimized',
  quality = 0.7,
  maxDimension = 1200,
  targetSizeKb,
  introTitle = 'Why Use an Image Compressor?',
  intro = 'Image compression is crucial for speeding up websites, saving storage, and improving user experience. Large image files can slow down load times and negatively impact SEO rankings and conversion rates.',
  useCases = [
    'Compress images before uploading to your website or blog',
    'Optimize product photos for faster ecommerce performance',
    'Resize and shrink personal photos for sharing',
  ],
  faq = defaultFaq,
}) => {
  const [originalImage, setOriginalImage] = useState(null);
  const [optimizedImage, setOptimizedImage] = useState(null);
  const [originalSize, setOriginalSize] = useState(0);
  const [optimizedSize, setOptimizedSize] = useState(0);
  const [outputInfo, setOutputInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const canvasRef = useRef(null);

  const inputId = useMemo(
    () => `image-input-${canonicalPath.replace(/[^a-z0-9]/gi, '-')}`,
    [canonicalPath]
  );

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    return () => {
      if (optimizedImage) {
        URL.revokeObjectURL(optimizedImage);
      }
    };
  }, [optimizedImage]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderBlob = async (img, width, height, type, imageQuality) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = width;
    canvas.height = height;

    if (type === 'image/jpeg') {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, width, height);
    } else {
      ctx.clearRect(0, 0, width, height);
    }

    ctx.drawImage(img, 0, 0, width, height);
    return canvasToBlob(canvas, type, imageQuality);
  };

  const optimizeImage = async (img) => {
    setLoading(true);
    setError('');

    try {
      let { width, height } = getScaledDimensions(img, maxDimension);
      let finalQuality = quality;
      let finalBlob;

      if (targetSizeKb) {
        const targetBytes = targetSizeKb * 1024;
        const qualitySteps = [0.86, 0.78, 0.7, 0.62, 0.54, 0.46, 0.38];

        for (let resizePass = 0; resizePass < 6; resizePass += 1) {
          for (const step of qualitySteps) {
            const blob = await renderBlob(img, width, height, outputType, step);
            finalBlob = blob;
            finalQuality = step;

            if (blob.size <= targetBytes) {
              resizePass = 6;
              break;
            }
          }

          if (finalBlob?.size <= targetBytes) {
            break;
          }

          width = Math.max(320, Math.round(width * 0.88));
          height = Math.max(320, Math.round(height * 0.88));
        }
      } else {
        finalBlob = await renderBlob(img, width, height, outputType, quality);
      }

      if (optimizedImage) {
        URL.revokeObjectURL(optimizedImage);
      }

      setOptimizedImage(URL.createObjectURL(finalBlob));
      setOptimizedSize(finalBlob.size);
      setOutputInfo({ width, height, quality: finalQuality });
    } catch (err) {
      setError(err.message || 'Unable to optimize this image');
    } finally {
      setLoading(false);
    }
  };

  const processFile = (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file.');
      return;
    }

    if (acceptedTypes?.length && !acceptedTypes.includes(file.type)) {
      setError(`This page accepts ${accept.replaceAll(',', ', ')} files.`);
      return;
    }

    setOriginalSize(file.size);
    setOptimizedImage(null);
    setOptimizedSize(0);
    setOutputInfo(null);
    setError('');

    const reader = new FileReader();
    reader.onerror = () => setError('Unable to read this image file.');
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setOriginalImage({
          src: event.target.result,
          width: img.width,
          height: img.height,
        });
        optimizeImage(img);
      };
      img.onerror = () => setError('Unable to load this image.');
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (e) => {
    processFile(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    processFile(e.dataTransfer.files[0]);
  };

  const downloadOptimizedImage = () => {
    if (!optimizedImage) return;

    const link = document.createElement('a');
    link.href = optimizedImage;
    link.download = `optimized-image.${outputExtension}`;
    link.click();
  };

  const savedPercent = originalSize
    ? (((originalSize - optimizedSize) / originalSize) * 100).toFixed(1)
    : '0.0';

  const relatedTools = toolPages.filter((tool) => tool.path !== canonicalPath).slice(0, 4);
  const hasResult = Boolean(optimizedImage && !loading);
  const savingsWidth = `${Math.max(0, Math.min(100, Number(savedPercent))).toFixed(0)}%`;

  return (
    <div className="page pt-32">
      <Seo
        title={metaTitle}
        description={metaDescription}
        path={canonicalPath}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: title,
          url: `${SITE_URL}${canonicalPath}`,
          image: DEFAULT_LOGO,
          applicationCategory: 'MultimediaApplication',
          operatingSystem: 'Web',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
          },
          description: metaDescription,
        }}
      />

      <div>
        <section className="section-hd center">
          {canonicalPath !== '/tool' && <div className="pill pill-cyan" style={{ marginBottom: 12 }}>{title}</div>}
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </section>

        <div className="step-row">
          <div className="step">
            <div className={`step-num${originalImage ? ' done' : ''}`}>{originalImage ? '✓' : '1'}</div>
            <div className="step-name">Upload</div>
          </div>
          <div className="step-line"></div>
          <div className="step">
            <div className={`step-num${!originalImage ? ' muted' : hasResult ? ' done' : ''}`}>{hasResult ? '✓' : '2'}</div>
            <div className={`step-name${!originalImage ? ' muted' : ''}`}>Optimize</div>
          </div>
          <div className="step-line"></div>
          <div className="step">
            <div className={`step-num${!hasResult ? ' muted' : ''}`}>3</div>
            <div className={`step-name${!hasResult ? ' muted' : ''}`}>Download</div>
          </div>
        </div>

        <section
          className="upload-zone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept={accept}
            onChange={handleImageUpload}
            style={{ display: 'none' }}
            id={inputId}
          />
          <label htmlFor={inputId} style={{ cursor: 'pointer', display: 'block' }}>
            <div className="upload-icon"><Upload /></div>
            <div className="upload-title">Drop image here or click to choose</div>
            <div className="upload-sub">{accept === 'image/*' ? 'JPEG · PNG · WebP · GIF · AVIF' : accept.replaceAll('image/', '').replaceAll(',', ' · ').toUpperCase()}</div>
            <div className="upload-formats">
              <span className="fmt-badge">{outputExtension.toUpperCase()}</span>
              {targetSizeKb && <span className="fmt-badge">{targetSizeKb}KB target</span>}
              <span className="fmt-badge">Private</span>
            </div>
          </label>
        </section>

        {!originalImage && (
          <div className="privacy-card">
            <div className="privacy-icon"><Lock /></div>
            <div>
              <div className="privacy-title">Your images never leave your device</div>
              <div className="privacy-text">All compression runs inside your browser tab. No file is sent to any server.</div>
            </div>
          </div>
        )}

        {error && (
          <div className="privacy-card" style={{ background: '#fee2e2', borderColor: '#fecaca' }}>
            {error}
          </div>
        )}

        {loading && (
          <section className="processing-state">
            <div className="spinner"></div>
            <div className="proc-title">Analyzing and compressing...</div>
            <div className="proc-sub">{(originalSize / 1024).toFixed(2)} KB original file</div>
            <div className="proc-bar"><div className="proc-fill"></div></div>
          </section>
        )}

        {originalImage && hasResult && (
          <>
            <section className="savings-bar">
              <div>
                <div className="savings-num">{(optimizedSize / 1024).toFixed(2)} KB</div>
                <div className="savings-label">Optimized from {(originalSize / 1024).toFixed(2)} KB</div>
              </div>
              <div className="progress-track"><div className="progress-fill" style={{ width: savingsWidth }}></div></div>
              <div className="savings-pct">{savedPercent}% smaller</div>
            </section>

            <section className="result-grid">
              <div className="result-panel">
                <div className="result-panel-header">
                  <span className="panel-label">Original</span>
                  <span className="panel-size">{(originalSize / 1024).toFixed(1)} KB</span>
                </div>
                <div className="result-img-area">
                  <img src={originalImage.src} alt="Original" />
                </div>
                <div style={{ padding: '10px 16px', color: 'var(--text-muted)', fontSize: 12 }}>
                  {originalImage.width} x {originalImage.height}px
                </div>
              </div>

              <div className="result-panel optimized">
                <div className="result-panel-header">
                  <span className="panel-label optimized">{outputLabel}</span>
                  <span className="panel-size optimized">{(optimizedSize / 1024).toFixed(1)} KB</span>
                </div>
                <div className="result-img-area">
                  <img src={optimizedImage} alt={outputLabel} />
                </div>
                <div style={{ padding: '10px 16px', color: 'var(--text-muted)', fontSize: 12 }}>
                  {outputInfo?.width} x {outputInfo?.height}px · quality {Math.round((outputInfo?.quality || quality) * 100)}
                </div>
              </div>
            </section>

            <div className="quality-control">
              <div className="qc-row">
                <span className="qc-label">Output</span>
                <div className="progress-track"><div className="progress-fill" style={{ width: savingsWidth }}></div></div>
                <span className="qc-val">{outputExtension.toUpperCase()}</span>
              </div>
            </div>

            <div className="download-btn-wrap">
              <button onClick={downloadOptimizedImage} className="btn-download">
                <Download size={17} />
                Download {outputExtension.toUpperCase()} ({(optimizedSize / 1024).toFixed(2)} KB)
              </button>
              {targetSizeKb && optimizedSize > targetSizeKb * 1024 && (
                <p style={{ marginTop: 10, textAlign: 'center', color: '#d97706', fontSize: 13 }}>
                  This image is still above {targetSizeKb}KB. Try a smaller source image if the upload form requires a strict limit.
                </p>
              )}
            </div>
          </>
        )}

        <canvas ref={canvasRef} style={{ display: 'none' }} />

        <div className="divider"></div>

        <section className="article-wrap">
          <div className="section-hd">
            <h2>{introTitle}</h2>
            <p>{intro}</p>
          </div>
          <div className="article-body">
            <h2>Best uses</h2>
            <ul>
              {useCases.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p>
              The workflow is private by design: choose an image, let your browser create the optimized version, then download the new copy.
            </p>
          </div>
        </section>

        <section className="article-wrap">
          <div className="section-hd">
            <h2>Frequently asked questions</h2>
          </div>
          {[...faq, ...defaultFaq].slice(0, 5).map(({ question, answer }) => (
            <div className="faq-item" key={question}>
              <div className="faq-q">{question}</div>
              <div className="faq-a">{answer}</div>
            </div>
          ))}
        </section>

        {relatedTools.length > 0 && (
          <section style={{ marginTop: 40 }}>
            <div className="section-hd">
              <h2>Related image tools</h2>
              <p>Continue with another focused compression or conversion workflow.</p>
            </div>
            <div className="tools-grid">
              {relatedTools.map((tool) => (
                <Link key={tool.path} to={tool.path} className="tool-card">
                  <div className={`tool-card-icon ${getToolTone(tool.path)}`}>
                    <ImageIcon />
                  </div>
                  <div>
                    <div className="tool-name">{tool.title}</div>
                    <div className="tool-desc">{tool.subtitle}</div>
                  </div>
                  <div className="tool-arrow"><ArrowRight /></div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="back-to-top"
          aria-label="Back to top"
          title="Back to top"
        >
          <ArrowUp />
        </button>
      )}
    </div>
  );
};

export default ImageOptimizer;
